"use client";
import { useSalesContact } from "@/components/site/sales-contact-context";

import { useRouter, useSearchParams } from "next/navigation";
import { useSitePathname } from "@/components/site/site-prefix-context";
import { useState } from "react";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";

import {
  Dialog,
  DialogContent,
  DialogTitleBar,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";

import { PriceAmount, useSiteMoney } from "@/components/site/site-money";
import type { SiteVideoCourse } from "@/templates/site/types";

export function VideoBuyPanel({
  course,
  trialSeconds = 0,
  qrImage: defaultQrImage = "",
  qrCaption: defaultQrCaption = "",
  services = [],
  startHref = "",
}: {
  course: SiteVideoCourse;

  trialSeconds?: number;

  qrImage?: string;
  qrCaption?: string;

  services?: string[];

  startHref?: string;
}) {
  const { qrImage, qrCaption } = useSalesContact({ qrImage: defaultQrImage, qrCaption: defaultQrCaption });
  const t = useTranslations("site.video");
  const money = useSiteMoney();
  const router = useRouter();
  const pathname = useSitePathname();
  const searchParams = useSearchParams();
  const [qrOpen, setQrOpen] = useState(false);

  const [skuId, setSkuId] = useState(() => {
    const shared = searchParams.get("spec");
    const fromUrl = shared
      ? course.skus.find((sku) => sku.id === shared)
      : undefined;
    if (fromUrl) return fromUrl.id;

    const inStock = course.skus.filter((sku) => sku.inStock);
    const cheapest = inStock.reduce<(typeof inStock)[number] | null>(
      (best, sku) => (best === null || sku.salePrice < best.salePrice ? sku : best),
      null,
    );

    return (cheapest ?? course.skus[0])?.id ?? "";
  });

  function selectSku(id: string) {
    setSkuId(id);

    if (course.skus.length > 1) {
      const url = new URL(window.location.href);
      url.searchParams.set("spec", id);
      window.history.replaceState(null, "", url);
    }
  }

  const selected = course.skus.find((sku) => sku.id === skuId) ?? null;
  const soldOut = !selected || !selected.inStock;

  const showPrice = !course.unlocked || course.free;

  function handleBuy() {
    if (!selected) return;

    const query = new URLSearchParams({
      module: course.module,
      contentId: course.contentId,
      skuId: selected.id,
      next: pathname,
    });

    router.push(`/member/checkout/course/?${query.toString()}`);
  }

  return (
    <div className="goumai_ce">
      {/* 多套餐时先让人选，选中的那档才决定下面显示多少钱 */}
      {!course.unlocked && course.skus.length > 1 ? (
        <div className="goumai_ce_taocan">
          {course.skus.map((sku) => (
            <button
              key={sku.id}
              type="button"
              disabled={!sku.inStock}
              onClick={() => selectSku(sku.id)}
              className={sku.id === skuId ? "on" : ""}
            >
              {sku.name || t("standardPlan")}
              {sku.validDays > 0
                ? ` · ${t("periodDays", { count: sku.validDays })}`
                : ` · ${t("periodForever")}`}
            </button>
          ))}
        </div>
      ) : null}

      {/*
        价格与试看提示排一行：各占一行的话，多套餐课这一栏会比封面还高，
        把封面撑变形。窄屏放不下时由 flex-wrap 自己折下去。

        买过的课整行不出：右边按钮已经是「开始学习」，再写一句「已拥有本课程」
        是同一件事说两遍。免费课例外——「免费」是价格本身，不是状态重复。
      */}
      {showPrice ? (
        <div className="goumai_ce_hang flex-heng">
          <div className="goumai_ce_jiage">
            {course.free ? (
              <span>{t("freeBadge")}</span>
            ) : selected ? (
              <PriceAmount amount={money.priceValue(selected.salePrice)} />
            ) : (
              <span>{course.accessDenied ? course.requiredLevelName ? t("accessLevel", { level: course.requiredLevelName }) : t("accessRequired") : t("noSku")}</span>
            )}
          </div>

          {trialSeconds > 0 && !course.unlocked ? (
            <div className="goumai_ce_shikan">
              <i />
              <span>{t("trialHint")}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* 服务承诺排在价格与按钮之间：和产品页同一个位置关系——掏钱前要确认的保障，
          比详情正文里靠前得多 */}
      {services.length > 0 ? (
        <ul className="fuwu">
          {services.map((item) => (
            <li key={item}>
              <Check aria-hidden />
              {item}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="goumai_ce_anniu flex-heng">
        {/* 二维码取后台「基础设置」里配的那张；没配就退回滚到页脚的联系方式，
            别点了没反应 */}
        <a
          className="goumai_ce_zixun"
          href={qrImage ? "#" : "#footer-contact"}
          onClick={(event) => {
            if (!qrImage) return;
            event.preventDefault();
            setQrOpen(true);
          }}
        >
          {t("consult")}
        </a>
        {course.unlocked ? (
          <a className="goumai_ce_xiadan" href={startHref || "#course-catalog"}>
            {t("startLearning")}
          </a>
        ) : (
          <a
            className="goumai_ce_xiadan"
            href="#"
            aria-disabled={soldOut}
            onClick={(event) => {
              event.preventDefault();
              if (soldOut) return;
              handleBuy();
            }}
          >
            {soldOut && course.accessDenied && !course.skus.length ? t("accessRequired") : soldOut ? t("soldOut") : t("buyNow")}
          </a>
        )}
      </div>

      {/* 二维码弹层用项目的 Dialog，不再自己拿 div 拼遮罩：手写那版没有焦点陷阱、
          Esc 关不掉、背后的页面还能滚，这些 Dialog 都带着 */}
      <Dialog open={qrOpen && Boolean(qrImage)} onOpenChange={setQrOpen}>
        {/* 标题栏用 ui/dialog 的 DialogTitleBar，和后台弹窗是同一份实现；
            不放 DialogFooter：这里只是看一眼二维码，没有要点的操作按钮，
            放一个空底栏只是白占一条高度 */}
        <DialogContent className="max-w-[min(20rem,calc(100%-30px))] gap-0 p-0">
          <DialogTitleBar title={t("consult")} />
          <div className="px-4 py-5">
            {/* 二维码是方的，先按 1:1 把位置占住：不占位的话图解码完才撑出高度，
                弹窗会先矮一截再弹开。骨架垫在底下，图加载完再盖上去 */}
            <div className="relative mx-auto aspect-square w-60 max-w-full">
              {/* 骨架垫在底层，图片盖在上面：图没到时这里透出骨架，到了自然遮住。
                  不用 onLoad 切状态——图若命中缓存，挂载前就已经 complete，
                  那个事件根本不会触发，图会一直停在透明状态上。 */}
              <Skeleton className="absolute inset-0 rounded-md" />
              {/* 二维码来自后台上传，地址不固定，不走 next/image */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={qrImage}
                alt={qrCaption || t("consult")}
                className="relative h-full w-full rounded-md bg-popover object-contain"
              />
            </div>
            <p className="mt-3 text-center text-sm text-muted-foreground">
              {qrCaption || t("consultHint")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
